import React, { useState } from 'react';
import { submitTrialSignup } from '../../services/api';
import { trackTrialSignup } from '../../utils/analytics';
import './SignUp.css';

const SignUp = () => {
  const [serviceType, setServiceType] = useState('consumer'); // Default to consumer
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    companyName: '', // Only for fleet
    businessType: '' // Only for fleet
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }

    // First name validation
    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    // Last name validation
    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    // Fleet-specific validations
    if (serviceType === 'fleet') {
      if (!formData.companyName) {
        newErrors.companyName = 'Company name is required';
      } else if (formData.companyName.length < 2) {
        newErrors.companyName = 'Company name must be at least 2 characters';
      }

      if (!formData.businessType) {
        newErrors.businessType = 'Business type is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Submit the form using the API service
      const response = await submitTrialSignup(
        formData.email,
        serviceType,
        formData.firstName,
        formData.lastName,
        serviceType === 'fleet' ? formData.companyName : undefined,
        serviceType === 'fleet' ? formData.businessType : undefined
      );

      if (response.success) {
        setSubmitSuccess(true);
        
        // Track the successful signup with analytics
        trackTrialSignup(serviceType);
        
        // Reset form after success
        setFormData({
          email: '',
          firstName: '',
          lastName: '',
          companyName: '',
          businessType: ''
        });
      } else {
        setSubmitError(response.message || 'Failed to sign up for trial. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting trial signup:', error);
      setSubmitError('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="signup-page">
      <section className="hero">
        <div className="container">
          <h1>Start Your Free Trial</h1>
          <p>Join thousands of EV drivers and fleet managers using AnyVolt to manage their charging needs.</p>
        </div>
      </section>

      <section className="signup-content">
        <div className="container">
          {submitSuccess ? (
            <div className="success-message">
              <h2>Thank You!</h2>
              <p>Your trial signup was successful. You'll receive an email with instructions to get started.</p>
              <button 
                className="btn-secondary"
                onClick={() => setSubmitSuccess(false)}
              >
                Sign up for different plan
              </button>
            </div>
          ) : (
            <div className="signup-form-container">
              <div className="service-type-selector">
                <div 
                  className={`service-type-option ${serviceType === 'consumer' ? 'active' : ''}`}
                  onClick={() => setServiceType('consumer')}
                >
                  <h3>Consumer Plan</h3>
                  <p>Perfect for individual EV owners</p>
                  <div className="price">Free</div>
                  <ul>
                    <li>Find and reserve charging stations</li>
                    <li>Access to network of charging stations</li>
                    <li>Basic charging analytics</li>
                    <li>Mobile app access</li>
                  </ul>
                </div>
                
                <div 
                  className={`service-type-option ${serviceType === 'fleet' ? 'active' : ''}`}
                  onClick={() => setServiceType('fleet')}
                >
                  <h3>Fleet Plan</h3>
                  <p>For businesses with multiple EVs</p>
                  <div className="price">$49 <span>per site/month</span></div>
                  <ul>
                    <li>Site management tools</li>
                    <li>Advanced scheduling</li>
                    <li>Detailed reporting</li>
                    <li>Priority support</li>
                    <li>Custom branding</li>
                  </ul>
                </div>
              </div>
              
              <form className="signup-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && <div className="error-message">{errors.email}</div>}
                </div>
                
                <div className="form-row">
                  <div className="form-group half">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={errors.firstName ? 'error' : ''}
                    />
                    {errors.firstName && <div className="error-message">{errors.firstName}</div>}
                  </div>
                  
                  <div className="form-group half">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={errors.lastName ? 'error' : ''}
                    />
                    {errors.lastName && <div className="error-message">{errors.lastName}</div>}
                  </div>
                </div>
                
                {serviceType === 'fleet' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="companyName">Company Name *</label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        className={errors.companyName ? 'error' : ''}
                      />
                      {errors.companyName && <div className="error-message">{errors.companyName}</div>}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="businessType">Business Type *</label>
                      <select
                        id="businessType"
                        name="businessType"
                        value={formData.businessType}
                        onChange={handleInputChange}
                        className={errors.businessType ? 'error' : ''}
                      >
                        <option value="">Select business type</option>
                        <option value="Fleet">Fleet</option>
                        <option value="Ride Share">Ride Share</option>
                        <option value="Delivery">Delivery</option>
                        <option value="Municipal">Municipal</option>
                        <option value="Corporate">Corporate</option>
                        <option value="Other">Other</option>
                      </select>
                      {errors.businessType && <div className="error-message">{errors.businessType}</div>}
                    </div>
                  </>
                )}
                
                <div className="form-group">
                  <p className="disclaimer">By signing up, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>. You'll receive a 7-day free trial with full access to all features.</p>
                </div>
                
                {submitError && <div className="error-message form-error">{submitError}</div>}
                
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : `Start ${serviceType === 'consumer' ? 'Consumer' : 'Fleet'} Trial`}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SignUp;